import { AsyncPipe, CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { StepService } from '@lx/quote-tool/shared/services';
import { AgeGroup, DatePickerFieldDate, QuoteInput, QuoteSchool } from '@lx/quote-tool/shared/types';
import {
  CourseComponentsModule,
  DateListSelectorComponent,
  DatePickerFieldComponent,
  DestinationComponentsModule,
  FormFieldSelectorComponent,
  LanguageComponentsModule,
  WeeksSelectorComponent,
} from '@lx/quote-tool/shared/ui/components';
import { CourseDetailsLoggingDirective } from '@lx/quote-tool/shared/ui/directives';
import { filterSchools } from '@lx/quote-tool/shared/utils/school';
import { parseStringListFromConfigValue } from '@lx/shared/app-config';
import { CourseDates } from '@lx/shared/data-access/course';
import { CityDataService, CountryDataService } from '@lx/shared/data-access/destination';
import { City, CityExtended, Country, CourseType, PriceRequestFilters } from '@lx/shared/data-access/types';
import { GoogleAnalyticsClickEventDirective } from '@lx/shared/google-analytics';
import { getConfiguration } from '@lx/shared/tokens';
import { NumericCodeAndName } from '@lx/shared/types';
import { FormFieldComponent } from '@lx/shared/ui/form-field';
import { LoadingSpinnerComponent } from '@lx/shared/ui/loading-spinner';
import { deepClone, getDateFromString, getDateRange, getDateStringFromDate, getToday } from '@lx/shared/utils';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { PriceRequestFormGoogleAnalyticsEvent } from '../../../../../google-analytics';
import { retrieveAccommodationTypes } from '../../../../../shared/functions';
import { PriceRequestFiltersService } from '../../../../../shared/services';
import { PriceRequestStoreHandlers } from '../../../../../shared/types';
import { PriceRequestFormStore } from '../../../../../store/store';
import { SectionTemplateComponent } from '../../../../sections/section-template/section-template.component';

@Component({
  standalone: true,
  selector: 'lx-price-request-course-form',
  templateUrl: './price-request-course-form.component.html',
  styleUrls: ['./price-request-course-form.component.scss'],
  imports: [
    CommonModule,
    LanguageComponentsModule,
    DestinationComponentsModule,
    CourseComponentsModule,
    DateListSelectorComponent,
    LoadingSpinnerComponent,
    SectionTemplateComponent,
    WeeksSelectorComponent,
    DatePickerFieldComponent,
    AsyncPipe,
    FormFieldComponent,
    FormFieldSelectorComponent,
    InputTextareaModule,
    ReactiveFormsModule,
    FormsModule,
    GoogleAnalyticsClickEventDirective,
    CourseDetailsLoggingDirective,
  ],
})
export class PriceRequestCourseFormComponent implements OnInit {
  // Number of columns of the form, by default it will be 2 just to be as similar as the
  // original price request form
  @Input() columns = 2;

  // The form title (it could be empty)
  @Input() title = '';

  quoteInput$: Observable<QuoteInput> | undefined;
  specialRequests: string | undefined;
  selectedAccommodationType = <NumericCodeAndName>{};
  courseType: CourseType | undefined;
  selectedLanguage = '';
  availableCountries: Country[] = [];
  filteredCountries: Country[] = [];
  availableCities: CityExtended[] = [];
  availableCourseTypes: string[] = [];
  availableWeeks: number[] = [];
  disabledDates: Date[] = [];
  accommodationTypes = retrieveAccommodationTypes();
  dateListEnabled = false;
  availableStartDates: string[] = [];

  ageGroup: AgeGroup | undefined = undefined;

  loading = false;

  // Min date for selecting will be today (it is the same behaviour as it is in the normal variant)
  minDate = getToday();
  maxDate = getToday();
  firstAvailableDate = getToday();

  isStepValid$ = new BehaviorSubject(false);

  languageSelectorClickEvent$ = new BehaviorSubject<PriceRequestFormGoogleAnalyticsEvent>(PriceRequestFormGoogleAnalyticsEvent.BeginPriceRequestForm);

  private selectedCourseType: string | undefined;
  private allSchools: QuoteSchool[] = [];
  private destroySubscription$: Subject<void> = new Subject<void>();
  private priceRequestFilters: PriceRequestFilters | undefined;

  private readonly route = inject(ActivatedRoute);
  private readonly stepService = inject(StepService);
  private readonly countryService = inject(CountryDataService);
  private readonly cityService = inject(CityDataService);
  private readonly priceRequestStore = inject(PriceRequestFormStore);
  private readonly priceRequestFiltersService = inject(PriceRequestFiltersService);
  private readonly configuration = getConfiguration();
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.quoteInput$ = this.priceRequestStore.quoteInput$.pipe(
      tap((quoteInput: QuoteInput) => {
        this.selectedAccommodationType = this.getSelectedAccommodationType(quoteInput);
      }),
    );

    this.priceRequestStore.selectedCourseTypeAndSelectedLanguageAndDestinationCity$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ courseType, languageCode, destinationCityId }) => {
        this.selectedCourseType = courseType;
        this.selectedLanguage = languageCode;

        if (destinationCityId) {
          this.getPriceRequestFilters(Number(destinationCityId));
        }
      });

    this.priceRequestStore.destinationCountriesAndCourseType$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({ destinationCountries, courseType }) => {
      this.availableCountries = destinationCountries || [];
      const filteredSchools = filterSchools(this.allSchools, this.selectedLanguage)?.filter((school) => school?.courseTypes?.includes(courseType));
      this.filteredCountries = this.availableCountries?.filter((country) => filteredSchools?.some((school) => school?.country === country?.code));
    });

    this.priceRequestStore.destinationCities$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((destinationCities) => (this.availableCities = destinationCities || []));

    this.priceRequestStore.courseTypes$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((courseTypes) => (this.availableCourseTypes = courseTypes || []));

    this.priceRequestStore.specialRequests$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((specialRequests) => (this.specialRequests = specialRequests || ''));

    this.allSchools = deepClone(this.route.snapshot.data?.data?.schools || []);
    this.ageGroup = this.stepService.getActiveAgeGroup();
  }

  processSelectedLanguageChange(language: string) {
    // Track the language selection event - the first time the user selects a language the event will be `BeginPriceRequestForm` but for the rest, it will be `QuoteStep1`
    this.languageSelectorClickEvent$.next(PriceRequestFormGoogleAnalyticsEvent.QuoteStep1);

    this.priceRequestStore.quoteLanguage$
      .pipe(
        switchMap((languageCode: string) => this.countryService.getAllCountriesWithParameters({ courseLanguageCode: languageCode })),
        takeUntil(this.destroySubscription$),
      )
      .subscribe((countries: Country[]) => {
        this.availableCountries = countries;
        this.priceRequestStore.updateDestinationCountries(countries);
        this.destroySubscription$.next();
      });

    this.availableCourseTypes = this.getAvailablePriceRequestCourseTypes(language);
    this.applyInputChangeToStore('languageCode', language);
  }

  processSelectedCountryChanged(country: Country) {
    const availableSchools = this.allSchools?.filter(
      (school) =>
        school?.country === country?.code &&
        school?.courseTypes?.includes(this.courseType?.code?.toString()) &&
        school?.courseLanguages?.some((language) => language?.code === this.selectedLanguage),
    );
    this.loading = true;
    this.cityService
      .getAllCitiesWithParameters({
        courseLanguageCode: this.selectedLanguage,
        countryCode: country?.code,
      })
      .pipe(takeUntil(this.destroySubscription$))
      .subscribe((availableCities: CityExtended[]) => {
        this.loading = false;
        this.availableCities = availableCities?.filter((city) => availableSchools?.some((school) => school?.city === city?.code));
        this.priceRequestStore.updateDestinationCities(this.availableCities);
        this.destroySubscription$.next();
      });
    this.applyInputChangeToStore('country', { country, countryCode: country?.code });
  }

  processSelectedDestinationCityChanged(city: City) {
    this.getPriceRequestFilters(Number(city?.code || ''));
    this.applyInputChangeToStore('destinationCity', city);
  }

  processSelectedCourseTypeChanged(courseType: CourseType) {
    this.courseType = courseType;
    this.filteredCountries = this.availableCountries;
    this.applyInputChangeToStore('courseType', courseType);
  }

  processSelectedStartDateChange(selectedStartDate: DatePickerFieldDate) {
    this.handleStartDateSelection(selectedStartDate?.dateString);
  }

  handleStartDateSelection(dateString: string | undefined) {
    if (!dateString) {
      this.availableWeeks = [];
      this.applyInputChangeToStore('startDate', undefined);
      return;
    }
    const dates = this.priceRequestFilters?.startDates?.courseDates.filter((date) => date.from === dateString);
    if (dates?.some((date) => date.length === null)) {
      // if there is even only one unbounded date -> set the available weeks based on the min and max duration
      this.setAvailableWeeks(this.priceRequestFilters?.durationMin, this.priceRequestFilters?.durationMax);
    } else {
      // if all dates have a length, set the available weeks based on the possible options
      this.availableWeeks = dates.map((date) => date.length).filter((length) => !!length);
    }
    this.applyInputChangeToStore('startDate', dateString);
  }

  processSelectedAccommodationTypeChanged(accommodationType: NumericCodeAndName) {
    this.selectedAccommodationType = accommodationType;
    this.applyInputChangeToStore('accommodationType', this.selectedAccommodationType?.name || '');
  }

  setSpecialRequests(specialRequestsText: string) {
    this.specialRequests = specialRequestsText;
    this.applyInputChangeToStore('specialRequests', this.specialRequests);
  }

  applyInputChangeToStore<K extends keyof PriceRequestStoreHandlers>(key: K, value: Parameters<PriceRequestStoreHandlers[K]>[0]): void {
    const handler = this.priceRequestStoreHandlers[key] as (arg: typeof value) => void;
    handler(value);
  }

  private priceRequestStoreHandlers: PriceRequestStoreHandlers = {
    languageCode: (value) =>
      this.priceRequestStore.updateQuoteInput({
        languageCode: value,
        country: undefined,
        countryCode: undefined,
        destinationCity: undefined,
        courseType: undefined,
        tuitionWeeks: undefined,
        startDate: undefined,
      }),
    courseType: (courseType) => {
      this.priceRequestStore.updateQuoteInput({ courseType: courseType?.code });
      this.priceRequestStore.updateCourseType(courseType);
    },
    country: ({ country, countryCode }) =>
      this.priceRequestStore.updateQuoteInput({
        country,
        countryCode,
        destinationCity: undefined,
        tuitionWeeks: undefined,
        startDate: undefined,
      }),
    destinationCity: (city) =>
      this.priceRequestStore.updateQuoteInput({
        destinationCity: city,
        tuitionWeeks: undefined,
        startDate: undefined,
      }),
    startDate: (dateString) => this.priceRequestStore.updateQuoteInput({ startDate: dateString }),
    tuitionWeeks: (weeks) => this.priceRequestStore.updateQuoteInput({ tuitionWeeks: weeks }),
    accommodationType: (type) => this.priceRequestStore.updateQuoteInput({ accommodationType: type }),
    specialRequests: (requests) => this.priceRequestStore.updateStudentDetails({ specialRequests: requests }),
  };

  private getPriceRequestFilters(cityId: number) {
    this.disabledDates = [];
    this.priceRequestFiltersService
      .getPriceRequestFilters(this.selectedLanguage, cityId, Number(this.selectedCourseType || ''))
      .pipe(takeUntil(this.destroySubscription$))
      .subscribe((priceRequestFilters: PriceRequestFilters) => {
        this.loading = false;
        this.priceRequestFilters = priceRequestFilters;

        // Set the dateListEnabled flag from backend
        this.dateListEnabled = priceRequestFilters?.startDates?.showDatesAsList || false;

        // Extract date strings for the date-list-selector
        this.availableStartDates = priceRequestFilters?.startDates?.courseDates?.map((courseDate) => courseDate.from) || [];

        // Filter the accommodation types based on the selected accommodations in the price request filters.
        const accommodationTypesCatalogue = retrieveAccommodationTypes();
        this.accommodationTypes = accommodationTypesCatalogue.filter((type) => this.priceRequestFilters?.accommodations?.includes(type.key));
        // Update the duration max value based on the price request filters.
        this.setAvailableWeeks(this.priceRequestFilters?.durationMin, this.priceRequestFilters?.durationMax);

        // Only prepare dates for calendar if not showing as list
        if (!this.dateListEnabled) {
          this.prepareDates();
        }

        this.destroySubscription$.next();
      });
  }

  private prepareDates() {
    const courseDates = this.priceRequestFilters?.startDates?.courseDates ?? [];

    // Set the min and max dates based on the first and last start dates
    this.minDate = this.getMinDate(courseDates);
    this.maxDate = this.getMaxDate(courseDates);

    // Create an array of disabled dates based on the possible start dates and the min/max dates
    this.disabledDates = this.getDisabledDates(courseDates);

    // Set first available date so the calendar component starts from there
    this.firstAvailableDate = this.getFirstAvailableStartDate(courseDates);
  }

  private getMinDate(courseDates: CourseDates[]): Date {
    const minDate = courseDates?.map((date) => date.from).reduce((min, item) => (min < item ? min : item), '');

    return minDate ? getDateFromString(minDate) : getToday();
  }

  private getMaxDate(courseDates: CourseDates[]): Date {
    const maxDate = courseDates?.map((date) => date.from).reduce((max, item) => (max > item ? max : item), '');

    return maxDate ? getDateFromString(maxDate) : getToday();
  }

  // Remove all valid dates from the range to get which ones are disabled
  private getDisabledDates(courseDates: CourseDates[]): Date[] {
    return getDateRange(this.minDate, this.maxDate)?.filter(
      (rangeDate) => !courseDates?.some((dateRange) => dateRange.from === getDateStringFromDate(rangeDate)),
    );
  }

  private getFirstAvailableStartDate(courseDates: CourseDates[]): Date {
    const today = new Date();
    const validStartDates = (courseDates ?? [])
      .filter(({ from }) => Boolean(from))
      .map(({ from }) => getDateFromString(from))
      .filter((startDate) => startDate.getTime() >= today.getTime());

    if (validStartDates.length === 0) {
      return this.minDate;
    }

    validStartDates.sort((aDate, bDate) => aDate.getTime() - bDate.getTime());
    return validStartDates[0];
  }

  private setAvailableWeeks(min: number, max: number) {
    this.availableWeeks = Array.from({ length: max - min + 1 }, (_, i) => i + min);
  }

  private getAvailablePriceRequestCourseTypes(languageCode: string): string[] {
    const schoolCourseTypes = filterSchools(this.allSchools, languageCode).reduce((acc: string[], { courseTypes }) => {
      if (!courseTypes) {
        return acc;
      }

      // Filter course types that are allowed for price requests and not already included in the accumulator
      return [...acc, ...courseTypes.filter((courseType) => !acc.includes(courseType))];
    }, []);

    const priceRequestAllowedCourseTypes = parseStringListFromConfigValue(this.configuration.PRICE_REQUEST_ALLOWED_COURSE_TYPES);
    const filteredCourseTypes = schoolCourseTypes.filter((courseType) => priceRequestAllowedCourseTypes.includes(courseType));
    if (filteredCourseTypes.length === 0) {
      // If no course types are available, return the original schools' course types
      return schoolCourseTypes;
    } else {
      // If there are available course types, return the filtered ones
      return filteredCourseTypes;
    }
  }

  private getSelectedAccommodationType(quoteInput: QuoteInput): NumericCodeAndName {
    if (quoteInput?.accommodationType) {
      return this.accommodationTypes.find((type) => type.name === quoteInput.accommodationType);
    }
  }
}
